import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PenSquare, Image, Tag, Save, X, AlertCircle, Link as LinkIcon, Info } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';

interface BlogEditorProps {
  isEdit?: boolean;
  post?: {
    id: string;
    title: string;
    slug: string;
    excerpt: string;
    content: string;
    image_url: string;
    category: string;
    tags: string[];
    meta_title: string;
    meta_description: string;
    meta_keywords: string[];
    published: boolean;
  };
}

function BlogEditor({ isEdit = false, post }: BlogEditorProps) {
  const navigate = useNavigate();
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(true);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: post?.title || '',
    excerpt: post?.excerpt || '',
    content: post?.content || '',
    images: post?.images || [],
    video_url: post?.video_url || '',
    category: post?.category || 'Бизнес',
    tags: post?.tags || [],
    meta_title: post?.meta_title || '',
    meta_description: post?.meta_description || '',
    meta_keywords: post?.meta_keywords || [],
    published: post?.published || true,
    slug: post?.slug || ''
  });
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password === '5500') {
      setIsPasswordModalOpen(false);
    } else {
      setPasswordError('Неверный пароль');
    }
  };

  if (isPasswordModalOpen) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6">
        <div className="bg-white rounded-2xl p-8 w-full max-w-md">
          <h2 className="text-2xl font-semibold text-[#1d1d1f] mb-6">
            Введите пароль для доступа
          </h2>
          <form onSubmit={handlePasswordSubmit}>
            <div className="space-y-4">
              <div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0171E3] focus:border-transparent"
                  placeholder="Введите пароль"
                />
                {passwordError && (
                  <p className="mt-2 text-sm text-red-500">{passwordError}</p>
                )}
              </div>
              <button
                type="submit"
                className="w-full bg-[#0171E3] text-white py-3 px-6 rounded-lg hover:bg-[#0156aa] transition-colors font-medium"
              >
                Войти
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  const categories = ['Бизнес', 'Логистика', 'Таможня', 'Аналитика'];

  const transliterate = (text: string): string => {
    const cyrillicToLatin: { [key: string]: string } = {
      'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd',
      'е': 'e', 'ё': 'yo', 'ж': 'zh', 'з': 'z', 'и': 'i',
      'й': 'y', 'к': 'k', 'л': 'l', 'м': 'm', 'н': 'n',
      'о': 'o', 'п': 'p', 'р': 'r', 'с': 's', 'т': 't',
      'у': 'u', 'ф': 'f', 'х': 'h', 'ц': 'ts', 'ч': 'ch',
      'ш': 'sh', 'щ': 'sch', 'ъ': '', 'ы': 'y', 'ь': '',
      'э': 'e', 'ю': 'yu', 'я': 'ya',
      'А': 'A', 'Б': 'B', 'В': 'V', 'Г': 'G', 'Д': 'D',
      'Е': 'E', 'Ё': 'Yo', 'Ж': 'Zh', 'З': 'Z', 'И': 'I',
      'Й': 'Y', 'К': 'K', 'Л': 'L', 'М': 'M', 'Н': 'N',
      'О': 'O', 'П': 'P', 'Р': 'R', 'С': 'S', 'Т': 'T',
      'У': 'U', 'Ф': 'F', 'Х': 'H', 'Ц': 'Ts', 'Ч': 'Ch',
      'Ш': 'Sh', 'Щ': 'Sch', 'Ъ': '', 'Ы': 'Y', 'Ь': '',
      'Э': 'E', 'Ю': 'Yu', 'Я': 'Ya'
    };
    
    return text
      .split('')
      .map(char => cyrillicToLatin[char] || char)
      .join('');
  };

  const generateSlug = (title: string): string => {
    if (!title) return '';
    
    // First transliterate Cyrillic characters to Latin
    const latinText = transliterate(title);
    
    return title
      .toLowerCase()
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .replace(/[^\w\-]+/g, '')
      .replace(/\-\-+/g, '-')
      .replace(/^-+/, '')
      .replace(/-+$/, '');
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value;
    const newSlug = generateSlug(transliterate(newTitle));
    setFormData(prev => ({ 
      ...prev, 
      title: newTitle,
      slug: newSlug
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      if (!formData.title) {
        throw new Error('Заголовок обязателен');
      }

      const slug = formData.slug || generateSlug(formData.title);
      if (!slug) {
        throw new Error('Невозможно создать URL для статьи');
      }

      const postData = { 
        ...formData
      };

      let result;
      if (isEdit && post?.id) {
        const { data: existingPost, error: checkError } = await supabase
          .from('blog_statia')
          .select('id')
          .eq('slug', slug)
          .neq('id', post.id)
          .maybeSingle();

        if (checkError) {
          throw checkError;
        }

        if (existingPost) {
          throw new Error('Статья с таким URL уже существует');
        }

        result = await supabase
          .from('blog_statia')
          .update(postData)
          .eq('id', post.id);
      } else {
        const { data: existingPost, error: checkError } = await supabase
          .from('blog_statia')
          .select('id')
          .eq('slug', slug)
          .maybeSingle();

        if (checkError) {
          throw checkError;
        }

        if (existingPost) {
          throw new Error('Статья с таким URL уже существует');
        }

        result = await supabase
          .from('blog_statia')
          .insert([postData]);
      }

      if (result.error) throw result.error;

      navigate('/blog');
    } catch (error: any) {
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-12 px-6">
      <div className="max-w-[980px] mx-auto">
        <div className="mb-8 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit ? 'Редактировать статью' : 'Новая статья'}
          </h1>
          <div className="flex gap-4">
            <a
              href="https://imgbb.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#0171E3] hover:text-[#0156aa] flex items-center"
            >
              <Image className="w-4 h-4 mr-1" />
              Загрузить фото
            </a>
            <a
              href="https://vkvideo.ru/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#0171E3] hover:text-[#0156aa] flex items-center"
            >
              <LinkIcon className="w-4 h-4 mr-1" />
              Загрузить видео
            </a>
          </div>
          <button
            onClick={() => navigate('/blog')}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start">
            <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="mb-6 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg flex items-start">
          <Info className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium mb-2">Форматирование текста:</p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li># Заголовок 1</li>
              <li>## Заголовок 2</li>
              <li>**Жирный текст**</li>
              <li>*Курсив*</li>
              <li>- Элемент списка</li>
            </ul>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Заголовок
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={handleTitleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0171E3] focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL статьи
                </label>
                <input
                  type="text"
                  value={formData.slug}
                  readOnly
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-500"
                />
                <p className="mt-1 text-sm text-gray-500">
                  URL генерируется автоматически из заголовка
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Краткое описание
                </label>
                <textarea
                  value={formData.excerpt}
                  onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0171E3] focus:border-transparent"
                  rows={3}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Содержание
                </label>
                <div className="mb-2 text-sm text-gray-500">
                  Используйте Markdown для форматирования текста
                </div>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0171E3] focus:border-transparent"
                  rows={10}
                  required
                  placeholder="# Заголовок&#10;&#10;Текст статьи..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Изображения (до 5 штук)
                </label>
                <div className="space-y-4">
                  {[...Array(5)].map((_, index) => (
                    <div key={index} className="flex gap-4">
                      <div className="flex-1 relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Image className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          value={formData.images[index] || ''}
                          onChange={(e) => {
                            const newImages = [...formData.images];
                            newImages[index] = e.target.value;
                            setFormData(prev => ({ ...prev, images: newImages }));
                          }}
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0171E3] focus:border-transparent"
                          placeholder="https://i.ibb.co/VpkrMWt5/image.jpg"
                        />
                      </div>
                      {formData.images[index] && (
                        <img
                          src={formData.images[index]}
                          alt={`Preview ${index + 1}`}
                          className="w-12 h-12 rounded object-cover"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL видео
                </label>
                <div className="flex gap-4">
                  <div className="flex-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <LinkIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={formData.video_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, video_url: e.target.value }))}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0171E3] focus:border-transparent"
                      placeholder="https://example.com/video.mp4"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Категория
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0171E3] focus:border-transparent"
                  required
                >
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Теги (через запятую)
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Tag className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={formData.tags.join(', ')}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      tags: e.target.value.split(',').map(tag => tag.trim()).filter(Boolean)
                    }))}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0171E3] focus:border-transparent"
                    placeholder="бизнес, логистика, китай"
                  />
                </div>
              </div>

              {/* SEO Fields */}
              <div className="border-t border-gray-200 pt-6 mt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">SEO настройки</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Meta Title
                    </label>
                    <input
                      type="text"
                      value={formData.meta_title}
                      onChange={(e) => setFormData(prev => ({ ...prev, meta_title: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0171E3] focus:border-transparent"
                      placeholder="SEO заголовок"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Meta Description
                    </label>
                    <textarea
                      value={formData.meta_description}
                      onChange={(e) => setFormData(prev => ({ ...prev, meta_description: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0171E3] focus:border-transparent"
                      rows={2}
                      placeholder="SEO описание"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Meta Keywords
                    </label>
                    <input
                      type="text"
                      value={formData.meta_keywords.join(', ')}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        meta_keywords: e.target.value.split(',').map(kw => kw.trim()).filter(Boolean)
                      }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0171E3] focus:border-transparent"
                      placeholder="ключевое слово 1, ключевое слово 2"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="published"
                  checked={formData.published}
                  disabled
                  className="h-4 w-4 text-[#0171E3] focus:ring-[#0171E3] border-gray-300 rounded"
                />
                <label htmlFor="published" className="ml-2 block text-sm text-gray-700">
                  Опубликовать
                </label>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-white bg-[#0171E3] hover:bg-[#0156aa] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0171E3] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-5 h-5 mr-2" />
              {isSubmitting ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default BlogEditor;